import React from "react";
import { HiCheck } from "react-icons/hi";

/**
 * Shared StepIndicator component used across all wizards.
 *
 * @param {Array}    steps        - Array of { id, label } objects
 * @param {number}   currentStep  - The active step id
 * @param {function} onStepClick  - Optional callback when a completed step is clicked
 * @param {"horizontal"|"vertical"} orientation - Layout direction (default: "horizontal")
 * @param {"default"|"compact"}     size        - Size variant (default: "default")
 */
const StepIndicator = ({
  steps = [],
  currentStep = 1,
  onStepClick,
  orientation = "horizontal",
  size = "default",
}) => {
  if (orientation === "vertical") {
    return (
      <VerticalStepper
        steps={steps}
        currentStep={currentStep}
        onStepClick={onStepClick}
        size={size}
      />
    );
  }

  return (
    <HorizontalStepper
      steps={steps}
      currentStep={currentStep}
      onStepClick={onStepClick}
      size={size}
    />
  );
};

/* ------------------------------------------------------------------ */
/*  Horizontal layout (PayrollRunWizard, SalaryAssignWizard, etc.)    */
/* ------------------------------------------------------------------ */
const HorizontalStepper = ({ steps, currentStep, onStepClick, size }) => {
  const isCompact = size === "compact";
  const circleSize = isCompact ? "w-8 h-8" : "w-10 h-10";
  const iconSize = isCompact ? "w-3.5 h-3.5" : "w-4 h-4";
  const fontSize = isCompact ? "text-xs" : "text-sm";

  return (
    <div className="w-full" role="navigation" aria-label="Progress steps">
      <ol className="flex items-start">
        {steps.map((step, index) => {
          const isCompleted = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          const isUpcoming = step.id > currentStep;
          const isLast = index === steps.length - 1;

          return (
            <li
              key={step.id}
              className={`flex items-start ${isLast ? "" : "flex-1"}`}
              aria-current={isCurrent ? "step" : undefined}
            >
              <div className={`flex items-start ${isLast ? "" : "w-full"}`}>
                {/* Step circle + label column */}
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={() => isCompleted && onStepClick && onStepClick(step.id)}
                    disabled={!isCompleted}
                    className={`
                      ${circleSize} rounded-full flex items-center justify-center ${fontSize} font-semibold transition-all flex-shrink-0
                      ${
                        isCompleted
                          ? "bg-primary-600 text-white shadow-sm cursor-pointer hover:bg-primary-700"
                          : isCurrent
                          ? "bg-white text-primary-600 border-2 border-primary-600 ring-[3px] ring-primary-100"
                          : "bg-gray-100 text-gray-400 border-2 border-gray-200 cursor-default"
                      }
                    `}
                    aria-label={`Step ${step.id}: ${step.label}`}
                  >
                    {isCompleted ? (
                      <HiCheck className={iconSize} />
                    ) : (
                      step.id
                    )}
                  </button>
                  <p
                    className={`mt-2 text-center leading-tight ${
                      isCompact ? "text-[11px] max-w-[70px]" : "text-xs max-w-[90px]"
                    } ${
                      isCurrent
                        ? "text-gray-900 font-semibold"
                        : isCompleted
                        ? "text-primary-600 font-medium"
                        : "text-gray-400"
                    }`}
                  >
                    {step.label}
                  </p>
                </div>

                {/* Connector line */}
                {!isLast && (
                  <div className={`flex-1 ${isCompact ? "mx-1.5" : "mx-2"} ${isCompact ? "mt-[15px]" : "mt-[19px]"}`}>
                    <div
                      className={`h-0.5 w-full rounded-full transition-colors ${
                        isCompleted ? "bg-primary-600" : "bg-gray-200"
                      }`}
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Vertical layout (OnboardingRoute sidebar)                         */
/* ------------------------------------------------------------------ */
const VerticalStepper = ({ steps, currentStep, onStepClick, size }) => {
  return (
    <div role="navigation" aria-label="Progress steps">
      <ol className="flex flex-col">
        {steps.map((step, index) => {
          const isCompleted = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          const isLast = index === steps.length - 1;

          return (
            <li key={step.id} className="flex items-start gap-3">
              {/* Circle + connector column */}
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => isCompleted && onStepClick && onStepClick(step.id)}
                  disabled={!isCompleted}
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all flex-shrink-0
                    ${
                      isCompleted
                        ? "bg-primary-600 text-white cursor-pointer hover:bg-primary-700"
                        : isCurrent
                        ? "bg-white text-primary-600 border-2 border-primary-600 ring-[3px] ring-primary-100"
                        : "bg-gray-100 text-gray-400 border-2 border-gray-200 cursor-default"
                    }
                  `}
                  aria-label={`Step ${step.id}: ${step.label}`}
                >
                  {isCompleted ? (
                    <HiCheck className="w-3.5 h-3.5" />
                  ) : (
                    step.id
                  )}
                </button>
                {!isLast && (
                  <div
                    className={`w-0.5 h-8 rounded-full my-1 ${
                      isCompleted ? "bg-primary-600" : "bg-gray-200"
                    }`}
                    aria-hidden="true"
                  />
                )}
              </div>

              {/* Label column */}
              <div className="pt-1">
                <p className="text-[11px] text-gray-400">Step {step.id}</p>
                <p
                  className={`text-sm leading-tight ${
                    isCurrent
                      ? "text-gray-900 font-semibold"
                      : isCompleted
                      ? "text-primary-600 font-medium"
                      : "text-gray-400"
                  }`}
                >
                  {step.label}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default StepIndicator;
